import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { useNavigate } from 'react-router-dom';
import { flattenMenuData } from '../../utils/flattenMenuData';
import { useAdminNavigation } from '../navigation/AdminNavigationContext';
import { getStoredRoles, hasAllowedRole } from '../../utils/authSession';

const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

const useClickOutside = (ref, handler) => {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }
      handler(event);
    };

    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);

    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [handler, ref]);
};

const QUICK_ACTIONS = [
  { label: 'Add Student', path: '/student/add', category: 'Actions' },
  { label: 'Add Employee', path: '/teacher/add', category: 'Actions' },
  { label: 'Add Fee Type', path: '/fee-types/create', category: 'Actions' },
  { label: 'Add School', path: '/register-school', category: 'Actions' },
  { label: 'Add Examination Type', path: '/examination-types/new', category: 'Actions' },
  { label: 'Add Class', path: '/classes', category: 'Actions' },
  { label: 'Add Subject', path: '/subjects', category: 'Actions' },
  { label: 'Add Timetable', path: '/timetable', category: 'Actions' },
  { label: 'Add Holiday', path: '/hdays', category: 'Actions' },
  { label: 'Add Event', path: '/events', category: 'Actions' },
  { label: 'Document Automation', path: '/document-automation', category: 'Actions' },
  { label: 'Generate Hall Tickets', path: '/hall-tickets', category: 'Actions' },
];

const RELATED_LINKS = [
  { label: 'Student Analytics', path: '/students/analytics', category: 'Related' },
  { label: 'Student Records', path: '/students/list', category: 'Related' },
  { label: 'Employee Payroll', path: '/payroll-dashboard', category: 'Related' },
  { label: 'Fee Management', path: '/fee-management', category: 'Related' },
  { label: 'Expense Dashboard', path: '/expenses', category: 'Related' },
  { label: 'Attendance Records', path: '/attendance', category: 'Related' },
  { label: 'Exam Results', path: '/academic-results', category: 'Related' },
  { label: 'School Settings', path: '/school-settings', category: 'Related' },
  { label: 'Leave Balance', path: '/leave-balance', category: 'Related' },
  { label: 'Library Management', path: '/library', category: 'Related' },
  { label: 'Transportation Overview', path: '/transportation/buses', category: 'Related' },
  { label: 'Event List', path: '/events', category: 'Related' },
  { label: 'Download Reports', path: '/reports', category: 'Related' },
  { label: 'Hall Tickets', path: '/hall-tickets', category: 'Related' },
  { label: 'Document Templates', path: '/document-templates', category: 'Related' },
];

const AdminQuickSearch = ({ handleDrawerToggle }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const searchContainerRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const { menuData } = useAdminNavigation();

  const userRoles = useMemo(() => getStoredRoles(), []);
  const showAdminActions = useMemo(
    () => hasAllowedRole(['SCHOOL_ADMIN', 'ADMIN', 'SUPER_ADMIN'], userRoles),
    [userRoles]
  );

  const flattenedMenuData = useMemo(() => flattenMenuData(menuData || []), [menuData]);

  const searchableItems = useMemo(() => {
    const dedupe = new Map();
    const upsert = (item) => {
      if (!item?.label || !item?.path) return;
      const key = `${item.label}|${item.path}|${item.category || 'Menu'}`;
      if (!dedupe.has(key)) {
        dedupe.set(key, item);
      }
    };

    flattenedMenuData.forEach((item) => {
      upsert({
        label: item.label,
        path: item.path,
        category: 'Menu',
      });
    });

    if (showAdminActions) {
      QUICK_ACTIONS.forEach(upsert);
      RELATED_LINKS.forEach(upsert);
    }

    return Array.from(dedupe.values());
  }, [flattenedMenuData, showAdminActions]);

  const performSearch = useCallback(
    (query) => {
      const normalizedQuery = query.trim().toLowerCase();
      setIsSearching(true);
      setSearchResults([]);

      if (!normalizedQuery) {
        setIsSearching(false);
        return;
      }

      const filteredResults = searchableItems.filter((item) =>
        item.label.toLowerCase().includes(normalizedQuery)
      );

      window.setTimeout(() => {
        setSearchResults(filteredResults);
        setIsSearching(false);
      }, 120);
    },
    [searchableItems]
  );

  const debouncedSearch = useMemo(() => debounce(performSearch, 220), [performSearch]);

  const handleSearchChange = useCallback(
    (event) => {
      const query = event.target.value;
      setSearchQuery(query);
      debouncedSearch(query);
    },
    [debouncedSearch]
  );

  const handleSearchSelect = useCallback(
    (path) => {
      setSearchQuery('');
      setSearchResults([]);
      setIsSearchFocused(false);
      navigate(path);
    },
    [navigate]
  );

  useClickOutside(searchContainerRef, () => {
    setIsSearchFocused(false);
  });

  const showSearchResults =
    isSearchFocused && (searchQuery.trim().length > 0 || searchResults.length > 0 || isSearching);

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <Box
        sx={{
          width: { xs: '100%', sm: 'auto' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 1,
        }}
      >
        {isMobile && handleDrawerToggle ? (
          <IconButton
            onClick={handleDrawerToggle}
            sx={{
              width: 38,
              height: 38,
              borderRadius: 1.5,
              flexShrink: 0,
              color: 'text.primary',
              bgcolor: alpha(theme.palette.background.paper, 0.96),
              boxShadow: `0 10px 24px ${alpha(theme.palette.common.black, 0.08)}`,
              border: `1px solid ${alpha(theme.palette.divider, 0.72)}`,
              '&:hover': {
                bgcolor: alpha(theme.palette.background.paper, 1),
              },
            }}
          >
            <MenuRoundedIcon fontSize="small" />
          </IconButton>
        ) : null}

        <Box
          ref={searchContainerRef}
          sx={{
            position: 'relative',
            width: { xs: '100%', sm: 300, md: 340 },
            maxWidth: '100%',
          }}
        >
          <TextField
            variant="outlined"
            placeholder="Search pages and actions"
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={() => setIsSearchFocused(true)}
            fullWidth
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 6,
                minHeight: 38,
                pr: 0.5,
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.paper, 0.92)
                    : alpha(theme.palette.common.white, 0.96),
                boxShadow: `0 12px 28px ${alpha(theme.palette.common.black, 0.08)}`,
                border: `1px solid ${alpha(theme.palette.divider, 0.72)}`,
                '& fieldset': {
                  border: 'none',
                },
                '&:hover': {
                  bgcolor:
                    theme.palette.mode === 'dark'
                      ? alpha(theme.palette.background.paper, 0.98)
                      : theme.palette.common.white,
                },
                '&.Mui-focused': {
                  bgcolor:
                    theme.palette.mode === 'dark'
                      ? alpha(theme.palette.background.paper, 1)
                      : theme.palette.common.white,
                  boxShadow: `0 16px 34px ${alpha(theme.palette.primary.main, 0.14)}`,
                  borderColor: alpha(theme.palette.primary.main, 0.58),
                },
              },
              '& .MuiInputBase-input': {
                py: 0.72,
                fontSize: '0.85rem',
              },
              '& .MuiInputBase-input::placeholder': {
                color: alpha(theme.palette.text.primary, 0.48),
                opacity: 1,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ ml: 0.55 }}>
                  <SearchRoundedIcon
                    sx={{
                      fontSize: 19,
                      color: alpha(theme.palette.text.primary, 0.55),
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: isSearching ? (
                <InputAdornment position="end">
                  <CircularProgress size={16} color="inherit" />
                </InputAdornment>
              ) : null,
            }}
          />

          {showSearchResults ? (
            <Paper
              elevation={0}
              sx={{
                position: 'absolute',
                top: 'calc(100% + 10px)',
                right: 0,
                left: 0,
                overflow: 'hidden',
                borderRadius: 1.5,
                border: `1px solid ${alpha(theme.palette.divider, 0.72)}`,
                boxShadow: `0 20px 46px ${alpha(theme.palette.common.black, 0.14)}`,
                maxHeight: '70vh',
                overflowY: 'auto',
                zIndex: theme.zIndex.modal - 1,
              }}
            >
              <Box sx={{ px: 1, py: 0.8 }}>
                {isSearching && searchResults.length === 0 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={22} />
                  </Box>
                ) : null}

                {!isSearching && searchQuery.trim() !== '' && searchResults.length === 0 ? (
                  <Typography sx={{ px: 1.5, py: 2, color: 'text.secondary', textAlign: 'center' }}>
                    No results found for "{searchQuery}"
                  </Typography>
                ) : null}

                {searchResults.length > 0 ? (
                  <List disablePadding>
                    {['Menu', 'Actions', 'Related'].map((category) => {
                      const categoryResults = searchResults.filter(
                        (result) => result.category === category
                      );

                      if (!categoryResults.length) {
                        return null;
                      }

                      return (
                        <React.Fragment key={category}>
                          <ListSubheader
                            disableSticky
                            sx={{
                              bgcolor: 'transparent',
                              color: 'text.secondary',
                              fontSize: '0.72rem',
                              fontWeight: 700,
                              letterSpacing: '0.08em',
                              lineHeight: '26px',
                              mt: category === 'Menu' ? 0 : 0.6,
                            }}
                          >
                            {category}
                          </ListSubheader>
                          {categoryResults.map((result) => (
                            <ListItem
                              key={`${result.label}-${result.path}`}
                              disablePadding
                              sx={{ borderRadius: 2, overflow: 'hidden' }}
                            >
                              <ListItemButton
                                onClick={() => handleSearchSelect(result.path)}
                                sx={{
                                  borderRadius: 2,
                                  px: 1.4,
                                  py: 0.85,
                                }}
                              >
                                <ListItemText
                                  primary={result.label}
                                  primaryTypographyProps={{
                                    fontSize: '0.88rem',
                                    fontWeight: 500,
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </List>
                ) : null}
              </Box>
            </Paper>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

AdminQuickSearch.propTypes = {
  handleDrawerToggle: PropTypes.func,
};

AdminQuickSearch.defaultProps = {
  handleDrawerToggle: undefined,
};

export default AdminQuickSearch;
